import {
  createStyles,
  TableCell,
  TableSortLabel,
  Theme,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import clsx from "clsx";
import React, { type CSSProperties, type ReactNode } from "react";

type OwnProps<TColId extends string> = {
  align?: "left" | "right" | "center";
  children: ReactNode;
  className?: string;
  id: TColId;
  isActionColumn?: boolean;
  isSortable: boolean;
  leadingElement?: ReactNode;
  onSortRequest: (id: TColId) => void;
  order?: "asc" | "desc";
  orderBy?: TColId;
  styles?: CSSProperties;
  trailingElement?: ReactNode;
};

type CustomTableHeaderCellImplProps<TColId extends string> = OwnProps<TColId> &
  WithStyles<typeof styles>;

function CustomTableHeaderCellImpl<TColId extends string>({
  align,
  children,
  classes,
  className,
  id,
  isActionColumn = false,
  isSortable,
  leadingElement,
  onSortRequest,
  order,
  orderBy,
  styles,
  trailingElement,
  ...rest
}: CustomTableHeaderCellImplProps<TColId>) {
  const customAlign = align ?? (isActionColumn ? "center" : "left");

  const cellStyles = {
    ...(isActionColumn ? { width: "5%" } : {}),
    ...(styles ?? {}),
  } satisfies CSSProperties;

  const cellClasses = clsx(classes.headerCell, className);

  if (!isSortable) {
    return (
      <TableCell
        align={customAlign}
        className={cellClasses}
        component="th"
        data-testid="table-header-cell"
        style={cellStyles}
        {...rest}
      >
        {leadingElement != null && leadingElement}
        {children}
        {trailingElement != null && trailingElement}
      </TableCell>
    );
  }

  const isSorted = orderBy === id;

  const handleSortRequest = () => {
    onSortRequest(id);
  };

  return (
    <TableCell
      align={customAlign}
      aria-sort={
        isSorted ? (order === "asc" ? "ascending" : "descending") : "none"
      }
      className={cellClasses}
      component="th"
      data-testid="table-header-sortable-cell"
      sortDirection={isSorted ? order : false}
      style={cellStyles}
      {...rest}
    >
      <TableSortLabel
        active={isSorted}
        className={classes.sortLabel}
        direction={isSorted ? order : "asc"}
        onClick={handleSortRequest}
      >
        {leadingElement != null && leadingElement}
        {children}
        {trailingElement != null && trailingElement}
      </TableSortLabel>
    </TableCell>
  );
}

const styles = (theme: Theme) =>
  createStyles({
    headerCell: {
      backgroundColor: "#e1e1e1",
      color: theme.palette.primary.main,
      fontSize: 14,
      fontWeight: 500,
      position: "sticky",
      textTransform: "uppercase",
      top: 0,
      zIndex: 10,
    },
    sortLabel: {
      color: theme.palette.primary.main,
      "&:hover": {
        color: theme.palette.primary.dark,
      },
      "&:focus": {
        color: theme.palette.primary.dark,
      },
    },
  });

const CustomTableHeaderCell = withStyles(styles)(CustomTableHeaderCellImpl) as <
  TColId extends string
>(
  props: OwnProps<TColId>
) => JSX.Element;

export default CustomTableHeaderCell;
